import React, { useEffect } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { Button } from "../ui/button";
import { Download, Play, Apple, Smartphone } from "lucide-react";
import { Reveal, RevealStagger, RevealChild } from "../motion/Reveal";

const APP_STORE =
  "https://apps.apple.com/us/app/sunscreened/id6756286578";

export default function CTA() {
  const [theme, setTheme] = React.useState(() => localStorage.getItem("theme") || "dark");
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const glow = useTransform(scrollYProgress, [0, 0.5, 1], [0.35, 0.75, 0.4]);

  useEffect(() => {
    const sync = () => setTheme(localStorage.getItem("theme") || "dark");
    window.addEventListener("themechange", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("themechange", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const dark = theme === "dark";

  return (
    <section
      ref={ref}
      className={`relative overflow-hidden py-20 md:py-28 ${
        dark ? "bg-[#060910]" : "bg-gradient-to-b from-white to-orange-50/50"
      }`}
    >
      <motion.div
        style={{ opacity: glow }}
        className={`pointer-events-none absolute inset-0 ${
          dark ? "bg-[radial-gradient(circle_at_30%_20%,rgba(249,115,22,0.22),transparent_45%)]" : ""
        }`}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <RevealStagger className="mb-16 text-center">
          <RevealChild>
            <div
              className={`mx-auto inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${
                dark
                  ? "border-white/10 bg-white/[0.05] text-amber-100/90"
                  : "border-orange-100 bg-white text-orange-800 shadow-sm"
              }`}
            >
              <Play className="h-4 w-4 text-orange-500" />
              See the flow
            </div>
          </RevealChild>
          <RevealChild>
            <h2 className="font-display mt-5 text-4xl font-semibold tracking-tight md:text-5xl">
              <span className={dark ? "text-slate-50" : "text-slate-900"}>A quick tour of </span>
              <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 bg-clip-text text-transparent">
                Sunscreened
              </span>
            </h2>
          </RevealChild>
          <RevealChild>
            <p className={`mx-auto mt-4 max-w-2xl text-lg ${dark ? "text-slate-400" : "text-slate-600"}`}>
              From check-in to calm guidance—watch how the pieces connect.
            </p>
          </RevealChild>
        </RevealStagger>

        <Reveal>
          <motion.div
            className="mx-auto max-w-5xl"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className={`overflow-hidden rounded-[1.75rem] border shadow-[0_24px_80px_rgba(249,115,22,0.14)] ${
                dark ? "border-white/10 bg-[#0a0f1a]" : "border-orange-100 bg-white"
              }`}
            >
              <div className="relative" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  className="absolute left-0 top-0 h-full w-full"
                  src="https://www.youtube.com/embed/TnrJUq6MK-A"
                  title="Sunscreened App Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </motion.div>
        </Reveal>

        <Reveal>
          <div className="mx-auto mt-16 max-w-4xl overflow-hidden rounded-[2rem] bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 p-[1px] shadow-2xl shadow-orange-500/25">
            <div className="rounded-[2rem] bg-gradient-to-br from-orange-600 via-rose-600 to-rose-700 p-10 text-center text-white md:p-14">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-medium backdrop-blur-md">
                <Download className="h-4 w-4" />
                Available on iOS
              </div>
              <h3 className="font-display mt-6 text-3xl font-semibold md:text-4xl">Ready for a softer relationship with the sun?</h3>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-white/90">
                Download Sunscreened and start with a two-minute setup. Android is on the roadmap—join the list inside
                the app.
              </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <motion.a href={APP_STORE} target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button size="lg" className="h-12 rounded-full bg-white px-8 text-base text-slate-900 hover:bg-white/90">
                    <Apple className="mr-2 h-5 w-5" />
                    Download for iOS
                  </Button>
                </motion.a>
                <Button
                  size="lg"
                  variant="outline"
                  disabled
                  className="h-12 rounded-full border-2 border-white/40 px-8 text-base text-white/80"
                >
                  <Smartphone className="mr-2 h-5 w-5" />
                  Android soon
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-white/80">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <svg key={i} className="h-4 w-4 fill-current" viewBox="0 0 20 20" aria-hidden>
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <span>Loved by early testers</span>
                </div>
                <div className="h-1 w-1 rounded-full bg-white/40" aria-hidden />
                <span>Growing community</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
